import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { ActivityDomainDtoIn } from '@/models/in/ActivityDomainDtoIn';
import { useDeleteAuthApi } from '@/hooks/UseApiCalls';

const QUERY_URL = 'management/activity-domains';

interface DeleteActivityDomainDialogProps {
  open: boolean;
  onClose: () => void;
  domain: ActivityDomainDtoIn;
  onSuccess: () => void;
}

const DeleteActivityDomainDialog = ({
  open,
  onClose,
  domain,
  onSuccess,
}: DeleteActivityDomainDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { mutate: archiveDomain, isPending: isDeleting } = useDeleteAuthApi<void>(
    `${QUERY_URL}/${domain.id}`
  );

  const handleDelete = () => {
    setError(null);
    archiveDomain(undefined, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: (err) => {
        console.error('Error archiving activity domain:', err);
        setError(t('activityDomainsManagement.dialog.errorDeleting'));
      },
    });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const confirmMessage = t('activityDomainsManagement.confirmDelete', { name: domain.name });
  const message = error ? `${confirmMessage}\n\n${error}` : confirmMessage;

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={t('activityDomainsManagement.delete')}
      message={message}
      confirmText={isDeleting ? 'saving' : 'activityDomainsManagement.deleteConfirm'}
      cancelText="activityDomainsManagement.deleteCancel"
      onConfirm={handleDelete}
      onCancel={handleClose}
      isLoading={isDeleting}
      variant="danger"
    />
  );
};

export default DeleteActivityDomainDialog;
